import { useMemo } from 'react';
import { makeStyles, Dropdown, Option, Spinner, Text } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useEffectiveTenant } from '@shared/hooks/useEffectiveTenant';
import { useAllTenants } from '@shared/hooks/useTenantCatalog';

const useStyles = makeStyles({
  dropdown: {
    minWidth: '220px',
  },
});

/**
 * Tenant switcher for the platform_admin in the admin header (Phase 51 D-02 / D-04).
 *
 * The effective tenant is preselected (the home tenant until a choice is made). Choosing the home
 * tenant clears the selection — the store maps it to "no selection". Renders nothing for anyone
 * else, and `useAllTenants` is disabled for them, so no request is sent.
 */
export function AdminTenantSelector() {
  const styles = useStyles();
  const { t } = useTranslation('admin');
  const { effectiveTenantId, homeTenantId, isPlatformAdmin, select } = useEffectiveTenant();
  const { data, isLoading, isError } = useAllTenants();

  const tenants = useMemo(
    () => [...(data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [data]
  );

  if (!isPlatformAdmin) return null;

  if (isLoading) return <Spinner size="tiny" />;

  if (isError) {
    return (
      <Text size={200} role="alert">
        {t('tenantContext.loadError')}
      </Text>
    );
  }

  const labelOf = (id: string, name: string) =>
    id === homeTenantId ? `${name}${t('tenantContext.homeSuffix')}` : name;

  const current = tenants.find((tenant) => tenant.id === effectiveTenantId);

  return (
    <Dropdown
      className={styles.dropdown}
      aria-label={t('tenantContext.selectorLabel')}
      placeholder={t('tenantContext.selectorPlaceholder')}
      value={current ? labelOf(current.id, current.name) : ''}
      selectedOptions={effectiveTenantId ? [effectiveTenantId] : []}
      onOptionSelect={(_, optionData) => {
        if (optionData.optionValue) select(optionData.optionValue);
      }}
    >
      {tenants.map((tenant) => (
        <Option key={tenant.id} value={tenant.id} text={labelOf(tenant.id, tenant.name)}>
          {labelOf(tenant.id, tenant.name)}
        </Option>
      ))}
    </Dropdown>
  );
}

export default AdminTenantSelector;
